import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getPackage,
  getCritiqueLines,
  getNarratives,
  generateFinancialReport,
  listTemplates,
} from '../../utils/financialAnalystApi';
import { getLocations } from '../../utils/locationApi';
import { computeGop } from './gop';
import { groupVarianceDrivers, deptTitle } from './varianceDrivers';
import './CommentaryPackagePrint.css';

// ---------- formatting helpers ----------
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthYear = (value) => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})/);
  if (m) return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
const monthParts = (value) => {
  const s = monthYear(value);
  const [mo, yr] = s.split(' ');
  return { month: mo || '', year: yr || '' };
};
const dayRange = (start, end) => {
  const f = (v) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    return `${MONTHS[parseInt(m[2], 10) - 1].slice(0, 3)} ${parseInt(m[3], 10)}`;
  };
  const a = f(start), b = f(end);
  const yr = (String(start || '').match(/^(\d{4})/) || [])[1] || '';
  return a && b ? `${a} – ${b}, ${yr}` : '';
};
const money = (n) => {
  const num = Number(n || 0);
  return (num < 0 ? '–$' : '$') + Math.abs(Math.round(num)).toLocaleString();
};
const signed = (n) => {
  const num = Number(n || 0);
  return (num >= 0 ? '+$' : '–$') + Math.abs(Math.round(num)).toLocaleString();
};
const pct = (n) => {
  const num = Number(n || 0);
  if (!isFinite(num)) return '—';
  return (num >= 0 ? '+' : '–') + Math.abs(num).toFixed(1) + '%';
};

// Strip markdown markers but keep paragraph structure (for prose rendering).
const cleanInlineMarkers = (t) => (t || '')
  .replace(/\*\*(.+?)\*\*/g, '$1')
  .replace(/\*(.+?)\*/g, '$1')
  .replace(/`(.+?)`/g, '$1');
const summarizeFinding = (t, maxLen = 320) => {
  const clean = (t || '')
    .replace(/^\s*([-*_])\1{2,}\s*$/gm, '')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (clean.length <= maxLen) return clean;
  const cut = clean.slice(0, maxLen);
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  return stop > 100 ? cut.slice(0, stop + 1) : cut.trim() + '…';
};

// ---------- markdown → editorial JSX (design classes) ----------
function renderInline(text, kp) {
  const out = [];
  let rest = text, k = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    if (m[2] !== undefined) {
      const isNum = /[\d$%]/.test(m[2]);
      out.push(<strong key={`${kp}b${k++}`} className={isNum ? 'num' : ''}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      out.push(<em key={`${kp}i${k++}`}>{m[3]}</em>);
    } else if (m[4] !== undefined) {
      out.push(<span key={`${kp}c${k++}`} className="gl">{m[4]}</span>);
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}
function renderMarkdown(md, keyBase = 'm') {
  const lines = (md || '').split('\n');
  const out = [];
  let list = null, key = 0;
  const flush = () => {
    if (!list) return;
    if (list.ordered) out.push(<ol key={`${keyBase}ol${key++}`}>{list}</ol>);
    else out.push(<ul key={`${keyBase}ul${key++}`}>{list}</ul>);
    list = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (/^([-*_])\1{2,}$/.test(line)) { flush(); continue; } // thematic break → skip in print body
    let m;
    if ((m = line.match(/^#{1,6}\s+(.*)/))) { flush(); out.push(<h3 key={`${keyBase}h${key++}`} className="subhead">{renderInline(m[1], `${keyBase}h${key}`)}</h3>); }
    else if ((m = line.match(/^\d+\.\s+(.*)/))) { if (!list || !list.ordered) { flush(); list = []; list.ordered = true; } list.push(<li key={`${keyBase}li${key++}`}>{renderInline(m[1], `${keyBase}li${key}`)}</li>); }
    else if ((m = line.match(/^[-*]\s+(.*)/))) { if (!list) list = []; list.push(<li key={`${keyBase}li${key++}`}>{renderInline(m[1], `${keyBase}li${key}`)}</li>); }
    else { flush(); out.push(<p key={`${keyBase}p${key++}`}>{renderInline(line, `${keyBase}p${key}`)}</p>); }
  }
  flush();
  return out;
}

// ---------- data helpers ----------
const PACKAGE_TEMPLATE_TYPES = new Set(['monthly_operating', 'quarterly_portfolio', 'variance', 'lender_compliance', 'investor_letter']);
const resolveTemplateType = (t) => {
  const cands = [t?.packageTemplateType, t?.templateType, t?.type, t?.key, t?.id, t?.name, t?.title].filter(Boolean).map(String);
  const explicit = cands.find(v => PACKAGE_TEMPLATE_TYPES.has(v));
  if (explicit) return explicit;
  const n = cands.join(' ').toLowerCase();
  if (n.includes('quarterly') || n.includes('portfolio')) return 'quarterly_portfolio';
  if (n.includes('variance')) return 'variance';
  if (n.includes('lender') || n.includes('compliance')) return 'lender_compliance';
  if (n.includes('investor')) return 'investor_letter';
  return 'monthly_operating';
};
const flagToneOf = (sev) => sev === 'red' ? 'critical' : sev === 'yellow' ? 'review' : sev === 'green' ? 'note' : null;

const SECTION_ORDER = {
  executive_summary: 1, financial_statements: 2, kpi_summary: 3,
  trend_analysis: 4, variance_drivers: 5, management_focus: 6,
  rooms_commentary: 7, fb_commentary: 8, undistributed_commentary: 9,
};
const getType = (n) => n?.sectionType || n?.section_type;
const getTitle = (n) => n?.sectionTitle || n?.section_title || 'Section';
const getContent = (n) => n?.content || '';

// ---------- small presentational pieces ----------
const BrandMark = () => <span className="page-head-brand-mark"><i /><i /><i /><i /></span>;
const PageHead = ({ label, property, period }) => (
  <div className="page-head">
    <div className="page-head-brand"><BrandMark /> {label}</div>
    <div className="page-head-meta"><b>{property}</b> · {period}</div>
  </div>
);
const PageFoot = ({ left, property, period }) => (
  <div className="page-foot">
    <span>{left}</span>
    <span className="mono">{property} · Commentary Package · {period}</span>
  </div>
);

// ---------- main document ----------
function CommentaryDocument({ pkg, critiqueLines, narratives, reportData, location }) {
  const property = location?.name || pkg?.title || 'Property';
  const period = monthYear(reportData?.period?.start || pkg?.periodStart);
  const { month, year } = monthParts(reportData?.period?.start || pkg?.periodStart);
  const range = dayRange(reportData?.period?.start || pkg?.periodStart, reportData?.period?.end || pkg?.periodEnd);

  // column keys
  const cols = reportData?.columns || [];
  const hasKey = (k) => cols.some(c => c.key === k);
  const actualKey = hasKey('actual') ? 'actual' : (cols[0]?.key || 'actual');
  const budgetKey = hasKey('budget') ? 'budget' : hasKey('plan') ? 'plan' : hasKey('forecast') ? 'forecast' : null;

  // totals
  const rev = reportData?.pnl?.revenue, exp = reportData?.pnl?.expense, net = reportData?.pnl?.netIncome;
  const revA = Number(rev?.totals?.[actualKey] || 0), revB = budgetKey ? Number(rev?.totals?.[budgetKey] || 0) : 0;
  const expA = Number(exp?.totals?.[actualKey] || 0), expB = budgetKey ? Number(exp?.totals?.[budgetKey] || 0) : 0;
  const netA = Number(net?.totals?.[actualKey] || (revA - expA));
  const netB = budgetKey ? Number(net?.totals?.[budgetKey] || (revB - expB)) : 0;
  const gop = computeGop(reportData, actualKey, budgetKey);
  const flagCount = critiqueLines.filter(l => l.flagSeverity === 'red' || l.flagSeverity === 'yellow').length;

  // critique lookups
  const critByAccount = useMemo(() => {
    const map = {};
    for (const c of critiqueLines) {
      const code = c.glAccountCode || c.accountNumber;
      if (code) map[String(code)] = c;
    }
    return map;
  }, [critiqueLines]);

  const sortedNarratives = useMemo(
    () => [...narratives].sort((a, b) => (SECTION_ORDER[getType(a)] ?? 99) - (SECTION_ORDER[getType(b)] ?? 99)),
    [narratives]
  );

  // page numbering: cover=1, toc=2, statement=3, narratives=4.., signoff=last
  const STATEMENT_PAGE = 3;
  const narrativeStart = STATEMENT_PAGE + 1;
  const signoffPage = narrativeStart + sortedNarratives.length;

  // ---- department roll-up for statement ----
  const deptBlocks = (section, isExpense) => {
    if (!section?.departments) return [];
    return section.departments.filter((dept) => {
      // Drop empty departments (no actual and no budget) to match the clean design layout.
      const a = Number(dept.subtotal?.[actualKey] || 0);
      const b = budgetKey ? Number(dept.subtotal?.[budgetKey] || 0) : 0;
      return a !== 0 || b !== 0;
    }).map((dept) => {
      const a = Number(dept.subtotal?.[actualKey] || 0);
      const b = budgetKey ? Number(dept.subtotal?.[budgetKey] || 0) : 0;
      const v = a - b;
      const fav = isExpense ? v < 0 : v > 0;
      const p = b !== 0 ? (v / Math.abs(b)) * 100 : null;
      // most material flagged line in dept → AI slip + danger flag
      const flagged = (dept.lines || [])
        .map(l => critByAccount[String(l.accountNumber)])
        .filter(c => c && c.aiNarrative)
        .sort((x, y) => Math.abs(Number(y.varianceBudget || 0)) - Math.abs(Number(x.varianceBudget || 0)));
      const slip = flagged[0];
      const danger = slip?.flagSeverity === 'red';
      return { label: dept.label, a, b, v, fav, p, slip, danger };
    });
  };
  const revDepts = deptBlocks(rev, false);
  const expDepts = deptBlocks(exp, true);

  const kpiTiles = [
    { cls: '', label: 'Total Revenue', value: money(revA), sub: budgetKey ? `${pct(revB ? ((revA - revB) / Math.abs(revB)) * 100 : 0)} vs ${money(revB)} budget` : '' , accent: 'success' },
    { cls: 'error', label: 'Total Expenses', value: money(expA), sub: budgetKey ? `${pct(expB ? ((expA - expB) / Math.abs(expB)) * 100 : 0)} vs ${money(expB)} budget` : '' },
    { cls: netA >= 0 ? 'success' : 'error', label: 'Net Income', value: money(netA), sub: budgetKey ? `vs ${money(netB)} budget` : '' },
    { cls: '', label: 'GOP Margin', value: gop.marginActual !== null ? gop.marginActual.toFixed(1) + '%' : '—', sub: budgetKey && gop.marginBudget !== null ? `${money(gop.actual)} GOP · vs ${gop.marginBudget.toFixed(1)}% budget` : (gop.actual ? `${money(gop.actual)} GOP` : '') },
    { cls: netA >= 0 ? 'success' : 'error', label: 'Variance to Budget', value: budgetKey ? signed(netA - netB) : '—', sub: 'net income vs plan' },
    { cls: 'warn', label: 'GL Flags', value: String(flagCount), sub: `${flagCount} flagged of ${critiqueLines.length} accounts` },
  ];

  const sectionRow = (label, a, b, isExpense) => {
    const v = a - b, fav = isExpense ? v < 0 : v > 0;
    const p = b !== 0 ? (v / Math.abs(b)) * 100 : null;
    return (
      <tr className="sub">
        <td>{label}</td>
        <td className="num">{money(a)}</td>
        <td className="num">{budgetKey ? money(b) : '—'}</td>
        <td className="num">{signed(v)}</td>
        <td className="num">{p === null ? '—' : pct(p)}</td>
      </tr>
    );
  };

  // Financial statement is a clean department roll-up — no inline AI commentary
  // (account-level commentary lives in the GL Variance Drivers section).
  const deptRows = (blocks) => blocks.map((d, i) => (
    <tr className="dept-row" key={`d${d.label}${i}`}>
      <td><span className="dept-name">{d.label}</span></td>
      <td className="num">{money(d.a)}</td>
      <td className="num">{budgetKey ? money(d.b) : '—'}</td>
      <td className="num"><span className={d.fav ? 'pos' : 'neg'}>{signed(d.v)}</span></td>
      <td className="num">{d.p === null ? '—' : pct(d.p)}</td>
    </tr>
  ));

  const glDriverGroups = groupVarianceDrivers(critiqueLines);

  return (
    <div className="doc">

      {/* ===== COVER ===== */}
      <section className="page cover">
        <div className="page-body">
          <div className="cover-top">
            <div className="logo-lockup"><span className="logo-mark"><i /><i /><i /><i /></span> Lodgeic</div>
            <div className="cover-corner">
              <b>{property.toUpperCase()}</b>
              {location?.address1 ? <><br />{location.address1}</> : null}
              {(location?.city || location?.state) ? <><br />{[location.city, location.state].filter(Boolean).join(', ')} {location?.zip || ''}</> : null}
            </div>
          </div>
          <div className="cover-mid">
            <div className="cover-eyebrow">Monthly P&amp;L Commentary Package</div>
            <h1 className="cover-title">{month}<br />{year}.</h1>
            <p className="cover-sub">
              Financial statement review, KPI summary, GL variance drivers, and an AI-drafted narrative — prepared for the ownership group from the property's general ledger.
            </p>
          </div>
          <div className="cover-bottom">
            <div className="cover-stats">
              <div className="cover-stat">
                <div className="cover-stat-label">Net Income</div>
                <div className={'cover-stat-value ' + (netA >= 0 ? 'pos' : 'neg')}>{money(netA)}</div>
                <div className="cover-stat-sub">{budgetKey ? `vs ${money(netB)} budget` : ''}</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-label">Total Revenue</div>
                <div className="cover-stat-value pos">{money(revA)}</div>
                <div className="cover-stat-sub">{budgetKey && revB ? `${pct(((revA - revB) / Math.abs(revB)) * 100)} vs budget` : ''}</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-label">Total Expenses</div>
                <div className="cover-stat-value">{money(expA)}</div>
                <div className="cover-stat-sub">{budgetKey && expB ? `${pct(((expA - expB) / Math.abs(expB)) * 100)} vs budget` : ''}</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-label">GL flags</div>
                <div className="cover-stat-value">{flagCount}</div>
                <div className="cover-stat-sub">{flagCount} unfavorable · review</div>
              </div>
            </div>
            <div className="cover-meta">
              <span className="cover-status">{(pkg?.status || 'draft').replace(/_/g, ' ')}</span>
              <span>Prepared by <b>Lodgeic AI</b></span>
              <span>Period: <b>{range || period}</b></span>
              <span style={{ marginLeft: 'auto' }} className="mono">PACKAGE-{pkg?.id}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TABLE OF CONTENTS ===== */}
      <section className="page draft">
        <PageHead label="Commentary Package" property={property} period={period} />
        <div className="page-body">
          <span className="h-eyebrow">Contents</span>
          <h2 className="section-title">What's in this package</h2>
          <p style={{ maxWidth: '60ch', marginTop: 8 }}>
            The financial statement, {sortedNarratives.length} narrative section{sortedNarratives.length === 1 ? '' : 's'} drafted from the P&amp;L and GL detail, and a sign-off appendix.
          </p>
          <div className="toc-list">
            <div className="toc-row">
              <span className="toc-num">01</span>
              <span className="toc-title">Financial Statement</span>
              <span className="toc-meta">Department roll-up</span>
              <span className="toc-page">{STATEMENT_PAGE}</span>
            </div>
            {sortedNarratives.map((n, i) => (
              <div className="toc-row" key={n.id}>
                <span className="toc-num">{String(i + 2).padStart(2, '0')}</span>
                <span className="toc-title">{getTitle(n)}</span>
                <span className="toc-meta">{getType(n) === 'variance_drivers' ? `${glDriverGroups.departments.reduce((s, g) => s + g.rows.length, 0)} material items` : 'Narrative'}</span>
                <span className="toc-page">{narrativeStart + i}</span>
              </div>
            ))}
            <div className="toc-row">
              <span className="toc-num">{String(sortedNarratives.length + 2).padStart(2, '0')}</span>
              <span className="toc-title">Sign-off</span>
              <span className="toc-meta">Acknowledgement</span>
              <span className="toc-page">{signoffPage}</span>
            </div>
          </div>
          <div className="pull">
            <div className="pull-eyebrow">Headline</div>
            <p>
              {period} posted a <strong>{money(netA)}</strong> net {netA < 0 ? 'loss' : 'income'}
              {budgetKey ? <> against a <strong>{money(netB)}</strong> budget</> : null}.
              {budgetKey
                ? (netA < 0
                    ? ((netA - netB) >= 0 ? ' Loss narrowed vs budget — see the variance drivers.' : ' Loss deepened vs budget — see the variance drivers.')
                    : ((netA - netB) >= 0 ? ' Favorable to plan.' : ' Unfavorable to plan — see the variance drivers.'))
                : ''}
            </p>
          </div>
        </div>
        <PageFoot left="Contents · 02" property={property} period={period} />
      </section>

      {/* ===== STATEMENT ===== */}
      <section className="page draft">
        <PageHead label="Section 01 · Financial Statement" property={property} period={period} />
        <div className="page-body">
          <span className="h-eyebrow">Section 01</span>
          <h2 className="section-title">Financial Statement</h2>
          <div className="h-kicker">Monthly P&amp;L · Department roll-up · {range}</div>
          {reportData?.pnl ? (
            <table className="pnl dept">
              <thead>
                <tr>
                  <th style={{ width: '42%' }}>Department</th>
                  <th className="num">Actual</th>
                  <th className="num">Budget</th>
                  <th className="num">Variance</th>
                  <th className="num">%</th>
                </tr>
              </thead>
              <tbody>
                {sectionRow('Revenue', revA, revB, false)}
                {deptRows(revDepts)}
                {sectionRow('Expenses', expA, expB, true)}
                {deptRows(expDepts)}
                <tr className="net">
                  <td>Net Income</td>
                  <td className="num">{money(netA)}</td>
                  <td className="num">{budgetKey ? money(netB) : '—'}</td>
                  <td className="num"><span className={(netA - netB) >= 0 ? 'pos' : 'neg'}>{signed(netA - netB)}</span></td>
                  <td className="num">{netB ? pct(((netA - netB) / Math.abs(netB)) * 100) : '—'}</td>
                </tr>
              </tbody>
            </table>
          ) : <p>P&amp;L data unavailable.</p>}
          <p style={{ marginTop: 14, fontSize: 11, color: 'var(--lodge-onyx-500)' }}>
            Department roll-up, budget basis. Account-level variance commentary appears in the <strong style={{ color: 'var(--lodge-onyx-700)' }}>GL Variance Drivers</strong> section.
          </p>
        </div>
        <PageFoot left="01 · Financial Statement · Page 03" property={property} period={period} />
      </section>

      {/* ===== NARRATIVE SECTIONS ===== */}
      {sortedNarratives.map((n, i) => {
        const type = getType(n);
        const pageNo = narrativeStart + i;
        return (
          <section className="page draft" key={n.id}>
            <PageHead label={`Section ${String(i + 2).padStart(2, '0')} · ${getTitle(n)}`} property={property} period={period} />
            <div className="page-body">
              <span className="h-eyebrow">Section {String(i + 2).padStart(2, '0')}</span>
              <h2 className="section-title">{getTitle(n)}</h2>
              <div className="h-kicker">Period: {range}</div>

              {type === 'kpi_summary' && (
                <>
                  <h3 className="subhead">Performance at a glance</h3>
                  <div className="kpi-grid">
                    {kpiTiles.map(t => (
                      <div className={'kpi-tile ' + t.cls} key={t.label}>
                        <div className="kpi-tile-label">{t.label}</div>
                        <div className="kpi-tile-value">{t.value}</div>
                        <div className="kpi-tile-sub">{t.sub}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {type === 'variance_drivers' ? (
                <>
                  {glDriverGroups.departments.length === 0 ? (
                    <p>No accounts missed budget materially this period — every flagged line is within tolerance or favorable.</p>
                  ) : glDriverGroups.departments.map((g) => (
                    <div className="gl-dept" key={g.department}>
                      <h3 className="gl-dept-title">{deptTitle(g.department)}</h3>
                      <table className="gl-dept-table">
                        <thead>
                          <tr>
                            <th>Line</th>
                            <th className="num">Actual</th>
                            <th className="num">Budget</th>
                            <th className="num">Variance $</th>
                            <th className="num">Variance %</th>
                            <th>Commentary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r.accountNumber}>
                              <td className="gl-line"><span className="gl-line-name">{r.accountName}</span><span className="gl-line-code">{r.accountNumber}</span></td>
                              <td className="num">{money(r.actual)}</td>
                              <td className="num">{money(r.budget)}</td>
                              <td className="num"><span className={r.favorable ? 'pos' : 'neg'}>{signed(r.variance)}</span></td>
                              <td className="num"><span className={r.favorable ? 'pos' : 'neg'}>{r.pct === null ? '—' : pct(r.pct)}</span></td>
                              <td className="gl-commentary">{summarizeFinding(r.commentary, 600)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {glDriverGroups.immaterial && (
                    <p className="gl-immaterial"><strong>Other immaterial variances:</strong> {glDriverGroups.immaterial.count} sub-materiality line{glDriverGroups.immaterial.count === 1 ? '' : 's'} netting {signed(glDriverGroups.immaterial.net)} to budget — individually below the materiality threshold; not itemized.</p>
                  )}
                </>
              ) : renderMarkdown(getContent(n), `n${i}`)}
            </div>
            <PageFoot left={`${String(i + 2).padStart(2, '0')} · ${getTitle(n)} · Page ${String(pageNo).padStart(2, '0')}`} property={property} period={period} />
          </section>
        );
      })}

      {/* ===== SIGN-OFF ===== */}
      <section className="page draft">
        <PageHead label={`Section ${String(sortedNarratives.length + 2).padStart(2, '0')} · Sign-off`} property={property} period={period} />
        <div className="page-body">
          <span className="h-eyebrow">Section {String(sortedNarratives.length + 2).padStart(2, '0')}</span>
          <h2 className="section-title">Sign-off</h2>
          <div className="h-kicker">Acknowledgement &amp; distribution</div>
          <p>This package was prepared from the property's general ledger and reviewed prior to distribution. All variance commentary is budget-basis.</p>
          <p>Reviewers are asked to acknowledge receipt and flag any items that require correction before close. Comments can be added directly to the source package in Lodgeic, or to the printed copy below.</p>
          <div className="signoff">
            <h3 className="signoff-title">Reviewers</h3>
            <div className="signoff-sub">Signing acknowledges review of the financial statement, KPI summary, GL variance drivers, and narrative commentary above.</div>
            <div className="signoff-rows">
              <div className="sig-row"><b>Reviewer 1</b>Name &amp; date</div>
              <div className="sig-row"><b>Reviewer 2</b>Name &amp; date</div>
              <div className="sig-row" style={{ marginTop: 32 }}><b>Reviewer 3</b>Name &amp; date</div>
              <div className="sig-row" style={{ marginTop: 32 }}><b>Reviewer 4</b>Name &amp; date</div>
            </div>
          </div>
          <p style={{ marginTop: 28, fontSize: 11, color: 'var(--lodge-onyx-500)' }}>
            <b style={{ color: 'var(--lodge-onyx-700)' }}>Disclosure.</b> Commentary is prepared from the property's general ledger to support financial review. It does not constitute audit, tax, or legal advice. Confirm all figures against source documents before distribution.
          </p>
        </div>
        <PageFoot left={`${String(sortedNarratives.length + 2).padStart(2, '0')} · Sign-off · Page ${String(signoffPage).padStart(2, '0')}`} property={property} period={period} />
      </section>
    </div>
  );
}

// ---------- standalone page (loads data) ----------
export default function CommentaryPackagePrint() {
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('packageId');
  const [state, setState] = useState({ loading: true, error: null, pkg: null, critiqueLines: [], narratives: [], reportData: null, location: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!packageId) { setState(s => ({ ...s, loading: false, error: 'Missing packageId.' })); return; }
      try {
        const [pkgRes, linesRes, narrRes] = await Promise.all([
          getPackage(parseInt(packageId)),
          getCritiqueLines(parseInt(packageId)),
          getNarratives(parseInt(packageId)),
        ]);
        const pkg = pkgRes.package || pkgRes;
        const critiqueLines = linesRes.critique_lines || linesRes || [];
        const narratives = narrRes.narratives || narrRes || [];

        const orgId = pkg?.orgId?.toString() || localStorage.getItem('currentOrgId');
        const locationId = pkg?.locationId?.toString() || searchParams.get('locationId');
        const start = pkg?.periodStart ? String(pkg.periodStart).slice(0, 10) : null;
        const end = pkg?.periodEnd ? String(pkg.periodEnd).slice(0, 10) : null;

        // resolve location + template, then fetch the P&L report
        let location = null, reportData = null;
        try {
          const locs = await getLocations();
          if (Array.isArray(locs)) location = locs.find(l => l.id?.toString() === locationId) || null;
        } catch (e) { /* address is best-effort */ }

        if (orgId && locationId && start && end) {
          let templateId = searchParams.get('templateId');
          if (!templateId) {
            try {
              const tplRes = await listTemplates(orgId);
              const templates = Array.isArray(tplRes) ? tplRes : (tplRes.templates || []);
              const want = resolveTemplateType(pkg);
              const match = templates.find(t => resolveTemplateType(t) === want) || templates[0];
              templateId = match?.id || null;
            } catch (e) { /* fall through */ }
          }
          if (templateId) {
            try { reportData = await generateFinancialReport(orgId, locationId, start, end, templateId); }
            catch (e) { console.error('Report load failed for export:', e); }
          }
        }

        if (!cancelled) setState({ loading: false, error: null, pkg, critiqueLines, narratives, reportData, location });
      } catch (err) {
        console.error('Failed to load export data:', err);
        if (!cancelled) setState(s => ({ ...s, loading: false, error: 'Failed to load package data.' }));
      }
    })();
    return () => { cancelled = true; };
  }, [packageId, searchParams]);

  const { loading, error, pkg, critiqueLines, narratives, reportData, location } = state;
  const property = location?.name || pkg?.title || 'Property';
  const period = monthYear(reportData?.period?.start || pkg?.periodStart);
  const pageCount = narratives.length + 4; // cover + toc + statement + N narratives + signoff

  return (
    <div className="cpp-root">
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-eyebrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6 4.6 1.9-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" /></svg>
            {property} · Commentary Package
          </span>
          {!loading && !error && <span className="toolbar-title">{property} · Monthly P&amp;L · {period}</span>}
          {!loading && !error && <span className="toolbar-meta">{pageCount} pages · {(pkg?.status || 'draft').replace(/_/g, ' ')}</span>}
        </div>
        <div className="toolbar-right">
          <button className="tb-btn" onClick={() => window.close()}>Close</button>
          <button className="tb-btn primary" onClick={() => window.print()} disabled={loading || !!error}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            Download PDF
          </button>
        </div>
      </div>

      {loading && <div className="cpp-status">Loading commentary package…</div>}
      {error && <div className="cpp-status">{error}</div>}
      {!loading && !error && (
        <CommentaryDocument
          pkg={pkg}
          critiqueLines={critiqueLines}
          narratives={narratives}
          reportData={reportData}
          location={location}
        />
      )}
    </div>
  );
}
